import React from 'react'
import { Button } from 'blaze-ui/Button'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import styles from './MultiStepPageLayout.module.scss'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import cx from 'classnames'
import { BlazeLogo } from './BlazeLogo'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { ArrowLeft } from 'blaze-ui/icons'

interface MultiStepPageLayoutProps {
  currentStep: number
  totalSteps: number
  onBack: () => void
  currentStepKey: string // used as the key for transition animations
  children: React.ReactNode
  onSkip?: () => void
  showBackButton?: boolean
  siderImage?: string
  siderComponent?: React.FC<any>
  direction?: 'forward' | 'backward'
  progressPercentage?: number
  fullWidth?: boolean
  fullScreen?: boolean
  backgroundClassName?: string
  showMobileLogo?: boolean
}

export const MultiStepPageLayout: React.FC<MultiStepPageLayoutProps> = ({
  currentStep,
  totalSteps,
  onBack,
  onSkip,
  currentStepKey,
  showBackButton = true,
  children,
  siderImage,
  siderComponent,
  direction = 'forward',
  progressPercentage,
  fullWidth = false,
  fullScreen = false,
  backgroundClassName,
  showMobileLogo = false,
}) => {
  const { isDesktop } = useResponsiveDesign()
  const slideForwardClassNames = {
    enter: styles['slide-enter-forward'],
    enterActive: styles['slide-enter-forward-active'],
    exit: styles['slide-exit-forward'],
    exitActive: styles['slide-exit-forward-active'],
  }

  const slideBackwardClassNames = {
    enter: styles['slide-enter-backward'],
    enterActive: styles['slide-enter-backward-active'],
    exit: styles['slide-exit-backward'],
    exitActive: styles['slide-exit-backward-active'],
  }

  const slideClassNames = direction === 'backward' ? slideBackwardClassNames : slideForwardClassNames
  const showSiderComponent = isDesktop

  return (
    <div className={cx(styles.page, backgroundClassName)}>
      {/* LEFT: Steps, main wizard content */}
      <div
        className={cx(styles.container, {
          [styles.transparentBackground]: backgroundClassName,
          [styles.fullWidth]: fullWidth,
          [styles.fullScreen]: fullScreen,
        })}
      >
        <ResponsiveDesign.Mobile>
          {((currentStep > 1 && showBackButton) || showMobileLogo || onSkip) && (
            <header
              className={cx(styles.mobileHeader, {
                [styles.transparentBackground]: backgroundClassName,
                [styles.invisible]: currentStepKey === 'confirmation',
              })}
            >
              <div className={styles.headerElement}>
                {currentStep > 1 && showBackButton && (
                  <IconButton
                    icon={() => <ArrowLeft size={24} />}
                    variant="tertiary"
                    aria-label="Back"
                    onPress={onBack}
                    className={styles.backButton}
                  />
                )}
              </div>
              {showMobileLogo && (
                <div className={styles.headerElement}>
                  <BlazeLogo width={86.2} height={24} />
                </div>
              )}
              <div className={styles.headerElement}>
                {onSkip && (
                  <Button variant="editor" aria-label="Skip" onPress={onSkip}>
                    Skip
                  </Button>
                )}
              </div>
            </header>
          )}
        </ResponsiveDesign.Mobile>

        <main
          className={cx(styles.main, {
            [styles.fullScreen]: fullScreen,
            [styles.withSiderComponent]: siderComponent && showSiderComponent,
          })}
        >
          <TransitionGroup
            className={cx(styles.transitionContainer, {
              [styles.fullWidth]: fullWidth,
              [styles.fullScreen]: fullScreen,
            })}
            id="transitionContainer"
          >
            <CSSTransition
              key={currentStepKey}
              classNames={{
                enter: styles['fade-enter'],
                enterActive: styles['fade-enter-active'],
                exit: styles['fade-exit'],
                exitActive: styles['fade-exit-active'],
              }}
              timeout={200}
              onEnter={(node: HTMLElement) => {
                // Reset scroll position when a new component enters
                const mainContainer = node.closest('main')
                if (mainContainer) {
                  mainContainer.scrollTop = 0
                }
                window.scrollTo(0, 0)
              }}
            >
              <div
                className={cx(styles.stepContent, { [styles.fullWidth]: fullWidth, [styles.fullScreen]: fullScreen })}
              >
                {children}
              </div>
            </CSSTransition>
          </TransitionGroup>
        </main>
      </div>

      {/* RIGHT: Sider containing the transitioning image */}
      {!fullWidth && (
        <ResponsiveDesign.Desktop>
          <div className={cx(styles.siderImageContainer, { [styles.hidden]: !siderImage })} id="siderImageContainer">
            <TransitionGroup component={null}>
              <CSSTransition
                key={siderImage}
                classNames={currentStep < 7 || direction === 'backward' ? slideClassNames : undefined}
                timeout={500}
              >
                <img src={siderImage} alt="Sider" className={styles.siderImage} />
              </CSSTransition>
            </TransitionGroup>
          </div>
        </ResponsiveDesign.Desktop>
      )}
      {/* RIGHT: Sider containing the helpful hints */}
      {siderComponent && showSiderComponent && (
        <ResponsiveDesign.Desktop>
          <div className={styles.siderComponentContainer} id="siderComponentContainer">
            <TransitionGroup component={null}>
              <CSSTransition
                key={`sider-component-${currentStepKey}`}
                classNames={currentStep < 7 || direction === 'backward' ? slideClassNames : undefined}
                timeout={500}
              >
                <div className={styles.siderComponent}>{React.createElement(siderComponent)}</div>
              </CSSTransition>
            </TransitionGroup>
          </div>
        </ResponsiveDesign.Desktop>
      )}
    </div>
  )
}
